import { useState } from "react"; 
import logo from "../assets/img/logo.png";
import logoMenor from "../assets/img/idus-logo-icone.png";
import Icons from "../assets/icons";
import Dropdown from "../components/Dropdown";
import { twMerge } from "tailwind-merge";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/Auth";
import { useNavigate, useLocation } from "react-router-dom"; // Importei useLocation para marcar ativo
import { ScrollArea } from "@radix-ui/react-scroll-area";
import UserProfileDialog from "@/components/UserProfileDialog";
import { checkHasPermission, checkHasAtLeastOnePermission } from "@/lib/permissions";

const Sidebar = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { usuario, logout } = useAuth();
  const [profileDialogOpen, setProfileDialogOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const location = useLocation();

  const userOptions = [
    {
      title: t("sidebar.drop.user.opts.profile"),
      action: () => {
        setProfileDialogOpen(true);
      },
    },
    {
      title: t("sidebar.drop.user.opts.logout"),
      action: () => {
        logout();
        navigate("/");
      },
    },
  ] as { title: string; action: () => void }[];

const adminOptions = [

    checkHasPermission(usuario?.permissoes, "ROLE_PERFIL_LISTAR") && {

      title: t("sidebar.drop.admin.opts.controlProfiles"),

      icon: <Icons.Groups pathStyle="fill-inherit" />, 

      action: () => navigate("/gestao-perfis"),

    },

    checkHasPermission(usuario?.permissoes, "ROLE_USUARIO_LISTAR") && {

      title: t("sidebar.drop.admin.opts.controlUsers"),

      icon: <Icons.Person pathStyle="fill-inherit" />, 

      action: () => navigate("/usuario"),

    },

     checkHasPermission(usuario?.permissoes, "ROLE_CLIENTE_LISTAR") && {

      title: t("sidebar.drop.admin.opts.controlClients"),

      icon: <Icons.Apartment pathStyle="fill-inherit" />, 

      action: () => navigate("/gestao-clientes"),

    },

    checkHasPermission(usuario?.permissoes, "ROLE_PRODUTO_LISTAR") && {

      title: t("sidebar.drop.admin.opts.controlProducts"),

      icon: <Icons.ServerTag pathStyle="fill-inherit" />, 

      action: () => navigate("/gestao-produtos"),

    },

    checkHasPermission(usuario?.permissoes, "ROLE_RECURSO_LISTAR") && {

      title: t("sidebar.drop.admin.opts.controlResources"),

      icon: <Icons.Source pathStyle="fill-inherit" />, 

      action: () => navigate("/gestao-recurso"),

    }

  ].filter(Boolean) as { title: string; action: () => void; icon?: React.ReactNode }[];

  const canViewAdminDropdown = checkHasAtLeastOnePermission(usuario?.permissoes, [
    "ROLE_PERFIL_LISTAR",
    "ROLE_USUARIO_LISTAR",
    "ROLE_CLIENTE_LISTAR",
    "ROLE_PRODUTO_LISTAR",
    "ROLE_RECURSO_LISTAR"
  ]);

  const asideContainerStyle = twMerge(
    "flex flex-col h-screen bg-neutro-50 text-neutro-700 shadow-xl transition-all duration-300 ease-in-out shrink-0 sticky top-0 z-20",
    open ? "w-80" : "w-24"
  );

  const headerStyle = "py-4 pb-12 border-b-1 border-gray-300";
  const headerPositionStyle = "flex items-center gap-4 justify-center relative";
  const headerFinalStyle = twMerge(headerPositionStyle, headerStyle);

  const hiddenButtonStyle =
    "duration-200 bg-primario-700 text-contraste-100 shadow-md px-2 py-1 rounded-sm cursor-pointer z-30";
  const hiddenButtonPositionStyle = "absolute top-20 -right-4"; 
  const hiddenButtonFinalStyle = twMerge(
    hiddenButtonStyle,
    hiddenButtonPositionStyle
  );

  const scrollAreaStyle =
    "flex-1 overflow-y-auto overflow-x-hidden [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-thumb]:bg-gray-300";

  // --- ESTILOS MANUAIS DO BOTÃO (Igual ao Dropdown) ---
  const navItemContainer = "border-y border-neutro-200 text-neutro-700 w-full";
  
  // Mesmas classes do Dropdown Button: p-4, min-h-[60px], hover:bg-gray-100
  const navItemButton = "duration-200 flex items-center justify-between font-medium w-full p-4 cursor-pointer min-h-[60px] transition-colors text-neutro-700 fill-neutro-700 hover:bg-gray-100 hover:text-primario-700 hover:fill-primario-700";
  
  // Estilo quando o item está ativo (opcional, para dar destaque)
  const activeClass = "bg-gray-100 text-primario-700 fill-primario-700 border-r-4 border-primario-700";

  return (
    <aside className={asideContainerStyle}>
        <header className={headerFinalStyle}>
          <div className={`transition-opacity duration-200 ${!open ? "opacity-0 w-0 overflow-hidden" : "opacity-100"}`}>
            <img src={logo} alt="Logo" className="max-w-[120px]" />
          </div>
          
          {!open && <img src={logoMenor} alt="Logo Small" className="w-8 h-8 object-contain absolute" />}

          <button
            className={hiddenButtonFinalStyle}
            onClick={() => setOpen(!open)}
          >
            {open ? (
              <Icons.ArrowLeft pathStyle="fill-contraste-100" />
            ) : (
              <Icons.ArrowRight pathStyle="fill-contraste-100" />
            )}
          </button>
        </header>

        <ScrollArea className={scrollAreaStyle}>
          <nav className="flex flex-col w-full">
            
            {/* ITEM 1: LICENÇAS (Manual, sem usar componente Button) */}
            <div className={navItemContainer}>
                <button 
                    onClick={() => navigate("/")}
                    className={twMerge(navItemButton, location.pathname === "/" ? activeClass : "")}
                    title={!open ? t("sidebar.btn.transaction.title") : ""}
                >
                    <div className={`flex items-center gap-3 overflow-hidden ${!open ? 'justify-center w-full' : ''}`}>
                        <span className="shrink-0 flex items-center justify-center">
                            <Icons.DatasetLinked pathStyle="fill-inherit" width={24} height={24} />
                        </span>
                        
                        <p className={`whitespace-nowrap transition-all duration-300 ${!open ? 'opacity-0 w-0 overflow-hidden' : 'opacity-100 w-auto'}`}>
                            {t("sidebar.btn.transaction.title")}
                        </p>
                    </div>
                </button>
            </div>

             {canViewAdminDropdown && (
              <Dropdown
                icon={<Icons.Groups pathStyle="fill-inherit"/>}
                label={t("sidebar.drop.admin.title")}
                options={adminOptions}
                responsive={!open}
                classProps="border-t-0 border-b border-neutro-200 w-full"
                onOpen={() => {
                  if (!open) setOpen(true);
                }}
                isSidebarOpen={open}
              />
            )}
          </nav>
        </ScrollArea>

        <footer>
          <Dropdown
            icon={<Icons.Person pathStyle="fill-inherit" width={24} height={24} />}
            label={usuario.nome ?? ""}
            options={userOptions}
            responsive={!open}
            onOpen={() => {
              if (!open) setOpen(true);
            }}
            isSidebarOpen={open}
          />
          <UserProfileDialog open={profileDialogOpen} setOpen={setProfileDialogOpen} />
          {open && (
             <p className="p-4 font-medium text-center text-xs text-gray-400 whitespace-nowrap">
                v {import.meta.env.VITE_PROJECT_VERSION}
             </p>
          )}
        </footer>
    </aside>
  );
};

export default Sidebar;